<div class="usuario-form-container">
  <!-- Header -->
  <div class="page-header">
    <div class="header-content">
      <h1 class="page-title">
        <i class="fas" [class.fa-user-plus]="!isEditMode" [class.fa-user-edit]="isEditMode"></i>
        {{ isEditMode ? 'Editar Usuario' : 'Nuevo Usuario' }}
      </h1>
      <button class="btn btn-secondary" (click)="onCancel()">
        <i class="fas fa-times"></i> Cancelar
      </button>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="loading">
    <i class="fas fa-spinner fa-spin"></i>
    <p>Cargando usuario...</p>
  </div>

  <!-- Error Alert -->
  <div class="alert alert-danger" *ngIf="error && !loading">
    <i class="fas fa-exclamation-triangle"></i>
    {{ error }}
  </div>

  <!-- Form -->
  <form [formGroup]="userForm" (ngSubmit)="onSubmit()" *ngIf="!loading">
    <div class="form-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fas fa-info-circle"></i> Información del Usuario
        </h3>
      </div>
      
      <div class="card-body">
        <div class="form-grid">
          <!-- Username -->
          <div class="form-group">
            <label for="username">
              <i class="fas fa-user"></i> Usuario *
            </label>
            <input
              type="text"
              id="username"
              formControlName="username"
              class="form-control"
              [class.is-invalid]="hasError('username')"
              placeholder="Nombre de usuario"
              autocomplete="username">
            <small class="form-text text-muted">
              Mínimo 3 caracteres, solo letras, números y guiones
            </small>
            <div class="invalid-feedback" *ngIf="hasError('username')">
              {{ getErrorMessage('username') }}
            </div>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label for="email">
              <i class="fas fa-envelope"></i> Email *
            </label>
            <input
              type="email"
              id="email"
              formControlName="email"
              class="form-control"
              [class.is-invalid]="hasError('email')"
              placeholder="correo@ejemplo.com"
              autocomplete="email">
            <small class="form-text text-muted">
              Dirección de correo electrónico válida
            </small>
            <div class="invalid-feedback" *ngIf="hasError('email')">
              {{ getErrorMessage('email') }}
            </div>
          </div>

          <!-- Password (full width) -->
          <div class="form-group form-group-full">
            <label for="password">
              <i class="fas fa-key"></i> Contraseña {{ isEditMode ? '(dejar en blanco para no cambiar)' : '*' }}
            </label>
            <div class="password-input-group">
              <input
                [type]="showPassword ? 'text' : 'password'"
                id="password"
                formControlName="password"
                class="form-control"
                [class.is-invalid]="hasError('password')"
                placeholder="Contraseña segura"
                autocomplete="new-password">
              
              <button
                type="button"
                class="btn btn-input-addon"
                (click)="togglePasswordVisibility()"
                title="Mostrar/Ocultar contraseña">
                <i class="fas" [class.fa-eye]="!showPassword" [class.fa-eye-slash]="showPassword"></i>
              </button>
              
              <button
                type="button"
                class="btn btn-input-addon"
                (click)="generatePassword()"
                title="Generar contraseña segura">
                <i class="fas fa-random"></i>
              </button>
              
              <button
                type="button"
                class="btn btn-input-addon"
                (click)="copyPasswordToClipboard()"
                [disabled]="!userForm.get('password')?.value"
                title="Copiar contraseña">
                <i class="fas fa-copy"></i>
              </button>
            </div>
            <small class="form-text text-muted">
              Mínimo 8 caracteres con mayúsculas, minúsculas y números
            </small>
            <div class="invalid-feedback" *ngIf="hasError('password')">
              {{ getErrorMessage('password') }}
            </div>
          </div>

          <!-- Role -->
          <div class="form-group">
            <label for="role">
              <i class="fas fa-shield-alt"></i> Rol *
            </label>
            <select
              id="role"
              formControlName="role"
              class="form-control"
              [class.is-invalid]="hasError('role')">
              <option *ngFor="let role of roles" [value]="role.value">
                {{ role.label }}
              </option>
            </select>
            <small class="form-text text-muted role-description">
              {{ getRoleDescription(userForm.get('role')?.value) }}
            </small>
            <div class="invalid-feedback" *ngIf="hasError('role')">
              {{ getErrorMessage('role') }}
            </div>
          </div>

          <!-- Nro Libreta -->
          <div class="form-group">
            <label for="nroLibreta">
              <i class="fas fa-id-card"></i> Nro Libreta (Opcional)
            </label>
            <div class="nro-libreta-input-group">
              <input
                type="number"
                id="nroLibreta"
                formControlName="nroLibreta"
                class="form-control"
                placeholder="Número de libreta"
                (blur)="searchEstudiante(userForm.get('nroLibreta')?.value)"
                min="1">
              
              <button
                type="button"
                class="btn btn-input-addon"
                (click)="clearNroLibreta()"
                *ngIf="userForm.get('nroLibreta')?.value"
                title="Limpiar">
                <i class="fas fa-times"></i>
              </button>
              
              <button
                type="button"
                class="btn btn-input-addon btn-primary"
                (click)="searchEstudiante(userForm.get('nroLibreta')?.value)"
                [disabled]="!userForm.get('nroLibreta')?.value || searchingEstudiante"
                title="Buscar estudiante">
                <i class="fas" [class.fa-search]="!searchingEstudiante" [class.fa-spinner]="searchingEstudiante" [class.fa-spin]="searchingEstudiante"></i>
              </button>
            </div>
            <small class="form-text text-muted">
              Vincular este usuario con un estudiante existente
            </small>
          </div>
        </div>

        <!-- Estudiante Card (if found) -->
        <div class="estudiante-preview" *ngIf="estudiante">
          <div class="alert alert-success">
            <i class="fas fa-check-circle"></i>
            <strong>Estudiante encontrado:</strong>
            {{ estudiante.apellido }}, {{ estudiante.nombre }} - Libreta #{{ estudiante.nroLibreta }}
          </div>
          <app-estudiante-card [estudiante]="estudiante"></app-estudiante-card>
        </div>

        <!-- Estudiante not found -->
        <div class="alert alert-warning" *ngIf="estudianteNotFound && !searchingEstudiante">
          <i class="fas fa-exclamation-triangle"></i>
          No se encontró un estudiante con ese número de libreta
        </div>
      </div>
    </div>

    <!-- Form Actions -->
    <div class="form-actions">
      <button type="button" class="btn btn-secondary" (click)="onCancel()" [disabled]="saving">
        <i class="fas fa-times"></i> Cancelar
      </button>
      <button type="submit" class="btn btn-primary" [disabled]="userForm.invalid || saving">
        <i class="fas" [class.fa-spinner]="saving" [class.fa-spin]="saving" [class.fa-save]="!saving"></i>
        {{ saving ? 'Guardando...' : (isEditMode ? 'Actualizar Usuario' : 'Crear Usuario') }}
      </button>
    </div>
  </form>
</div>